/**
 * Centralized Permission Utilities - Class-based approach
 * All permission checks should go through this PermissionChecker class
 *
 * Works with the permission object from useGetCurrentUser hook
 */

import { Permission } from '@/types/permissions.types';

/**
 * Type for the combined permissions object
 */
export type Permissions = Record<string, boolean> | undefined | null;

/**
 * PermissionChecker Class
 * Provides all permission checking methods in a clean, organized way
 */
export class PermissionChecker {
  private permissions: Permissions;

  constructor(permissions: Permissions) {
    this.permissions = permissions;
  }

  /**
   * Core permission checking method
   */
  has(permission: Permission | string): boolean {
    if (!this.permissions) return false;
    return this.permissions[permission] === true;
  }

  /**
   * Check if user has ANY of the specified permissions
   */
  hasAny(permissionList: (Permission | string)[]): boolean {
    return permissionList.some((perm) => this.has(perm));
  }

  /**
   * Check if user has ALL of the specified permissions
   */
  hasAll(permissionList: (Permission | string)[]): boolean {
    return permissionList.every((perm) => this.has(perm));
  }

  // ============================================
  // DASHBOARD PERMISSIONS
  // ============================================

  canViewDashboard(): boolean {
    return this.has('view_dashboard');
  }

  canViewDashboardAnalytics(): boolean {
    return this.has('view_dashboard_analytics');
  }

  canViewFollowup(): boolean {
    return this.has('view_followup');
  }

  canManageFollowup(): boolean {
    return this.has('manage_followup');
  }

  // ============================================
  // QUOTE PERMISSIONS
  // ============================================

  canViewQuotes(): boolean {
    return this.has('view_quotes');
  }

  canConvertQuoteToOrder(): boolean {
    return this.has('convert_quote_to_order');
  }

  canManageQuotesStatus(): boolean {
    return this.has('manage_quotes_status');
  }

  // ============================================
  // ORDER PERMISSIONS
  // ============================================

  canViewOrders(): boolean {
    return this.has('view_orders');
  }

  canManagePaymentStatus(): boolean {
    return this.has('manage_payment_status');
  }

  canManageOrderStatus(): boolean {
    return this.has('manage_order_status');
  }

  // ============================================
  // CUSTOMER PERMISSIONS
  // ============================================

  canViewCustomers(): boolean {
    return this.has('view_customers');
  }

  canCreateCustomers(): boolean {
    return this.has('create_customers');
  }

  canEditCustomers(): boolean {
    return this.has('edit_customers');
  }

  canDeleteCustomers(): boolean {
    return this.has('delete_customers');
  }

  canExportCustomers(): boolean {
    return this.has('export_customers');
  }

  // ============================================
  // ONBOARDING PERMISSIONS
  // ============================================

  canViewOnboarding(): boolean {
    return this.has('view_onboarding');
  }

  canCreateOnboarding(): boolean {
    return this.has('create_onboarding');
  }

  canUpdateUserOnboarding(): boolean {
    return this.has('update_user_onboarding');
  }

  // ============================================
  // CREDIT CONTROL PERMISSIONS
  // ============================================

  canViewCreditControl(): boolean {
    return this.has('view_credit_control');
  }

  canManageCreditLimits(): boolean {
    return this.has('manage_credit_limits');
  }

  canApproveCredit(): boolean {
    return this.has('approve_credit');
  }

  canViewCreditReports(): boolean {
    return this.has('view_credit_reports');
  }

  // ============================================
  // PRODUCT CONFIGURATOR PERMISSIONS
  // ============================================

  canViewProductConfigurator(): boolean {
    return this.has('view_product_configurator');
  }

  canManageProducts(): boolean {
    return this.has('manage_products');
  }

  canManagePricing(): boolean {
    return this.has('manage_pricing');
  }

  canManageOptions(): boolean {
    return this.has('manage_options');
  }

  canManageProductValidation(): boolean {
    return this.has('manage_product_validation');
  }

  canManageChatbotFlow(): boolean {
    return this.has('manage_chatbot_flow');
  }

  // ============================================
  // ANALYTICS PERMISSIONS
  // ============================================

  canViewAnalytics(): boolean {
    return this.has('view_analytics');
  }

  // ============================================
  // USER MANAGEMENT PERMISSIONS
  // ============================================

  canViewUsers(): boolean {
    return this.has('view_users');
  }

  canCreateUsers(): boolean {
    return this.has('create_users');
  }

  canEditUsers(): boolean {
    return this.has('edit_users');
  }

  canDeactivateUsers(): boolean {
    return this.has('deactivate_users');
  }

  canManageRoles(): boolean {
    return this.has('manage_roles');
  }

  canChangeRegion(): boolean {
    return this.has('change_region');
  }

  // ============================================
  // SETTINGS PERMISSIONS
  // ============================================

  canViewSettings(): boolean {
    return this.has('view_settings');
  }

  canViewPermissions(): boolean {
    return this.has('view_permissions');
  }

  canManagePermissions(): boolean {
    return this.has('manage_permissions');
  }

  // ============================================
  // PERMISSION MANAGEMENT PERMISSIONS
  // ============================================

  canAddRoles(): boolean {
    return this.has('add_roles');
  }

  canUpdateRoles(): boolean {
    return this.has('update_roles');
  }

  canAssignPermissionToRole(): boolean {
    return this.has('assign_permission_to_role');
  }

  // ============================================
  // DEPOT PERMISSIONS
  // ============================================

  canViewDepots(): boolean {
    return this.has('view_depots');
  }

  canManageDepots(): boolean {
    return this.has('manage_depots');
  }

  canAssignDepots(): boolean {
    return this.has('assign_depots');
  }

  // ============================================
  // CURRENCY EXCHANGE PERMISSIONS
  // ============================================

  canViewCurrencyExchange(): boolean {
    return this.has('view_currency_exchange');
  }

  canManageCurrencyExchange(): boolean {
    return this.has('manage_currency_exchange');
  }
}

// ============================================
// STANDALONE FUNCTIONS (for backward compatibility and convenience)
// ============================================

/**
 * Check if user has a specific permission
 */
export const hasPermission = (
  permissions: Permissions,
  permission: Permission | string
): boolean => {
  if (!permissions) return false;
  return permissions[permission] === true;
};

/**
 * Check if user has ANY of the specified permissions
 */
export const hasAnyPermission = (
  permissions: Permissions,
  permissionList: (Permission | string)[]
): boolean => {
  return permissionList.some((perm) => hasPermission(permissions, perm));
};

/**
 * Check if user has ALL of the specified permissions
 */
export const hasAllPermissions = (
  permissions: Permissions,
  permissionList: (Permission | string)[]
): boolean => {
  return permissionList.every((perm) => hasPermission(permissions, perm));
};

// ============================================
// CONVENIENCE FUNCTIONS FOR NAVIGATION
// ============================================

export const canViewDashboard = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_dashboard');
};

export const canViewQuotes = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_quotes');
};

export const canViewOrders = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_orders');
};

export const canViewCustomers = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_customers');
};

export const canViewOnboarding = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_onboarding');
};

export const canViewCreditControl = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_credit_control');
};

export const canViewProductConfigurator = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_product_configurator');
};

export const canViewAnalytics = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_analytics');
};

export const canViewUsers = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_users');
};

export const canViewSettings = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_settings');
};

export const canViewPermissions = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_permissions');
};

export const canViewDepots = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_depots');
};

export const canViewCurrencyExchange = (permissions?: Permissions): boolean => {
  return hasPermission(permissions, 'view_currency_exchange');
};
